// Community fix for Radix UI Tooltip not working on mobile web browsers
// Original issue: https://github.com/shadcn-ui/ui/issues/86#issuecomment-2743383068
// Fix: https://github.com/blockful/anticapture/issues/784#issuecomment-3045392183
//
// Do not close on trigger mouseleave — that breaks Radix hoverable content
// (pointer can move into the tooltip, including clickable children). Leave
// close-on-leave to Radix (grace area + TooltipContentHoverable).

"use client";

import * as React from "react";
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import { cn } from "../../lib/utils";

type TooltipEventsContextValue = {
	open: boolean;
	setOpen: React.Dispatch<React.SetStateAction<boolean>>;
	onClick: () => void;
	onMouseEnter: () => void;
	onTouchStart: () => void;
	onKeyDown: React.KeyboardEventHandler<HTMLButtonElement>;
};

const TooltipEventsContext = React.createContext<TooltipEventsContextValue>({
	open: false,
	setOpen: () => {},
	onClick: () => {},
	onMouseEnter: () => {},
	onTouchStart: () => {},
	onKeyDown: () => {},
});

function TooltipEventsProvider({ children }: { children: React.ReactNode }) {
	const [open, setOpen] = React.useState(false);

	const onClick = () => setOpen((prev) => !prev);
	const onMouseEnter = () => setOpen(true);
	const onTouchStart = () => setOpen((prev) => !prev);
	const onKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
		e.preventDefault();
		if (e.key === "Enter") {
			setOpen((prev) => !prev);
		}
	};

	return (
		<TooltipEventsContext.Provider
			value={{
				open,
				setOpen,
				onClick,
				onMouseEnter,
				onTouchStart,
				onKeyDown,
			}}
		>
			{children}
		</TooltipEventsContext.Provider>
	);
}

function useTooltipEvents() {
	const context = React.useContext(TooltipEventsContext);
	if (!context) {
		throw new Error(
			"useTooltipEvents must be used within a TooltipEventsProvider",
		);
	}
	return context;
}

export function TooltipProvider(
	props: React.ComponentProps<typeof TooltipPrimitive.Provider>,
) {
	return (
		<TooltipEventsProvider>
			<TooltipPrimitive.Provider {...props} />
		</TooltipEventsProvider>
	);
}

export function Tooltip(
	props: React.ComponentProps<typeof TooltipPrimitive.Root>,
) {
	const { open, setOpen } = useTooltipEvents();
	return (
		<TooltipPrimitive.Root open={open} onOpenChange={setOpen} {...props} />
	);
}

export function TooltipTrigger(
	props: React.ComponentProps<typeof TooltipPrimitive.Trigger>,
) {
	const { onClick, onMouseEnter, onTouchStart, onKeyDown } = useTooltipEvents();
	return (
		<TooltipPrimitive.Trigger
			onClick={onClick}
			onMouseEnter={onMouseEnter}
			onTouchStart={onTouchStart}
			onKeyDown={onKeyDown}
			{...props}
		/>
	);
}

export function TooltipContent({
	className,
	sideOffset = 8,
	...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
	return (
		<TooltipPrimitive.Portal>
			<TooltipPrimitive.Content
				sideOffset={sideOffset}
				className={cn(
					"z-50 overflow-hidden rounded-md border bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2",
					className,
				)}
				{...props}
			/>
		</TooltipPrimitive.Portal>
	);
}
